import { Box, Flex } from '@chakra-ui/react';
import { NAVBAR_HEIGHT } from 'constants/editor';
import { LEFT_MENU_OPENED } from 'constants/mixpanel';
import { useVenngageE1Gate } from 'hooks/useVenngageE1Gate';
import { Mixpanel } from 'libs/third-party/Mixpanel/mixpanel';
import { useAppDispatch, useAppSelector } from 'modules/Editor/store';
import { selectActiveWidgetMenu } from 'modules/Editor/store/editorSettingsSelector';
import { setActiveWidgetMenu } from 'modules/Editor/store/editorSettingsSlice';
import { useUser } from 'modules/User';
import { ReactElement, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { WIDGET_MENU_OPTIONS } from 'types/WidgetMenu.types';

import { SIDEBAR_ITEMS, SIDEBAR_WIDTH } from '../WidgetMenu.config';
import { Item } from './Item';
import { SIDE_BAR_ID } from './Sidebar.config';
import { handleSidebarEventType } from './Sidebar.helpers';

export const Sidebar = ({ readonly }: { readonly?: boolean }): ReactElement => {
  const dispatch = useAppDispatch();
  const { userType } = useUser();
  const activeWidgetMenu = useAppSelector(selectActiveWidgetMenu);
  const { venngageE2 } = useVenngageE1Gate();
  const isInCombineUploadAndImageExperiment = venngageE2?.experiments?.combineUploadAndImagesPanelExp === true;

  const { t } = useTranslation('editor_widget_menu', { useSuspense: false, keyPrefix: 'sideBar' });
  /**
   * Activates the selected widget menu.
   * If the activated menu and clicked menu are the same, set the WIDGET_MENU_OPTIONS.NONE to close the widget menu.
   *
   * @param itemId {WIDGET_MENU_OPTIONS}
   */
  const onClickSidebarItem = useCallback(
    (itemId: WIDGET_MENU_OPTIONS): void => {
      if (readonly) return;

      const itemType = handleSidebarEventType(itemId);

      //  only track if item type exists in widget menu options and the menu is not already opened
      if (itemType && !activeWidgetMenu) {
        Mixpanel.track(LEFT_MENU_OPENED.eventName, {
          from: LEFT_MENU_OPENED.from.LeftPanel,
          type: itemType,
        });
      }

      dispatch(setActiveWidgetMenu(itemId));
    },
    [activeWidgetMenu, dispatch, readonly],
  );

  return (
    <Flex
      width={SIDEBAR_WIDTH + 'px'}
      minWidth={SIDEBAR_WIDTH + 'px'}
      height={`calc(100vh - ${NAVBAR_HEIGHT}px)`}
      overflowY='auto'
      flexDirection='column'
      shadow='4px 0px 4px -4px rgb(0 0 0 / 30%)'
      zIndex='sideBar'
      data-testid='widget-menu-sidebar'
      gap={2}
      opacity={readonly ? 0.25 : 1}
      pointerEvents={readonly ? 'none' : 'auto'}
      id={SIDE_BAR_ID}
      justifyContent='flex-start'
    >
      {SIDEBAR_ITEMS.map((item, index) => {
        const isHidden = item.disabledUserTypes && item.disabledUserTypes.includes(userType);
        const isActive = activeWidgetMenu === item.id;
        const isFirstItem = index === 0;
        const isLastItem = index === SIDEBAR_ITEMS.length - 1;
        const onClick = () => onClickSidebarItem(item.id);

        if (isHidden) {
          return null;
        }

        if (
          isInCombineUploadAndImageExperiment &&
          (item.id === WIDGET_MENU_OPTIONS.PHOTOS || item.id === WIDGET_MENU_OPTIONS.UPLOAD)
        ) {
          return null;
        }

        if (!isInCombineUploadAndImageExperiment && item.id === WIDGET_MENU_OPTIONS.IMAGES) {
          return null;
        }

        return (
          <Box
            key={item.id}
            position='relative'
            marginTop={isFirstItem ? 'auto' : '0'}
            marginBottom={isLastItem ? 'auto' : '0'}
          >
            <Item
              id={item.id}
              label={t(`${item.id.replaceAll('-', '_')}.label` as keyof typeof t)}
              onClick={onClick}
              item={item}
              isActive={!readonly && isActive}
              readonly={readonly}
            />
          </Box>
        );
      })}
    </Flex>
  );
};
